/* =============================================================================
   Fines Foam — cart drawer behavior.

   Add, change and remove run against the Ajax Cart API. The drawer contents
   are then re-read from the server (snippets/ff-cart-items.liquid, rendered on
   the /cart page) and swapped in, so Liquid remains the single source of truth
   for money formatting, discounts and line properties.

   ponytail: one page fetch per cart mutation. The Section Rendering API would
   be lighter, but it can only render sections that exist on the page and the
   drawer is a snippet in theme.liquid. If the drawer ever becomes a section,
   swap refresh() for `sections: [...]` on the mutation request.

   Public API: window.FFCart.{ open, close, refresh, add, change }
   Events (via FF.emit): "cart:updated", "cart:error"
   ========================================================================== */
(function () {
  'use strict';

  var FF = window.FF || {};
  var routes = window.routes || {};
  var strings = window.cartStrings || {};

  var GENERIC_ERROR = strings.error || 'There was an error updating your cart. Please try again.';

  var drawer = null;
  var panel = null;
  var renderRegion = null;
  var errorBox = null;
  var liveBox = null;

  var releaseFocus = null;
  var inFlight = 0;

  /* ---------------------------------------------------------------------- */
  /* Helpers                                                                 */
  /* ---------------------------------------------------------------------- */

  function cacheNodes() {
    drawer = document.querySelector('[data-ff-cart-drawer]');
    if (!drawer) return false;
    panel = drawer.querySelector('[data-ff-cart-panel]');
    renderRegion = drawer.querySelector('[data-ff-cart-render]');
    errorBox = drawer.querySelector('[data-ff-cart-error]');
    liveBox = drawer.querySelector('[data-ff-cart-live]');
    return true;
  }

  function announce(message) {
    if (!liveBox || !message) return;
    /* Clearing first makes repeat messages re-announce. */
    liveBox.textContent = '';
    window.setTimeout(function () {
      liveBox.textContent = message;
    }, 60);
  }

  function showError(message) {
    var text = message || GENERIC_ERROR;
    if (errorBox) {
      errorBox.textContent = text;
      errorBox.hidden = false;
    }
    announce(text);
    if (FF.emit) FF.emit('cart:error', { message: text });
    /* Never swallow: the console keeps the original detail for debugging. */
    console.error('[FF cart]', text);
  }

  function clearError() {
    if (!errorBox) return;
    errorBox.hidden = true;
    errorBox.textContent = '';
  }

  function setBusy(busy) {
    inFlight += busy ? 1 : -1;
    if (inFlight < 0) inFlight = 0;
    if (drawer) drawer.classList.toggle('is-busy', inFlight > 0);
  }

  function setCount(value, isEmpty) {
    var count = String(value == null ? 0 : value).trim();
    document.querySelectorAll('[data-ff-cart-count]').forEach(function (node) {
      node.textContent = count;
      node.hidden = isEmpty;
    });
  }

  /* Turns a non-2xx or error-shaped Ajax Cart response into a thrown Error. */
  function readResponse(response) {
    return response
      .json()
      .catch(function () {
        throw new Error(GENERIC_ERROR);
      })
      .then(function (data) {
        if (!response.ok || data.status || data.errors) {
          var message = data.description || data.message;
          if (!message && data.errors) {
            message = typeof data.errors === 'string' ? data.errors : Object.keys(data.errors).join(', ');
          }
          throw new Error(message || GENERIC_ERROR);
        }
        return data;
      });
  }

  function postJSON(url, payload) {
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(payload)
    }).then(readResponse);
  }

  /* ---------------------------------------------------------------------- */
  /* Rendering                                                               */
  /* ---------------------------------------------------------------------- */

  /*
    Re-reads the cart page and transplants the freshly rendered drawer body
    and the header bubble. Everything numeric comes from the server.
  */
  function refresh() {
    if (!renderRegion) return Promise.resolve();

    return fetch(routes.cart_url || '/cart', {
      credentials: 'same-origin',
      cache: 'no-store'
    })
      .then(function (response) {
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');

        var nextBody = doc.querySelector('[data-ff-cart-render]');
        /*
          The drawer is in theme.liquid, so any page that renders the layout
          carries it — including a 404. Missing means the response was not a
          storefront page at all, which is a real failure.
        */
        if (!nextBody) throw new Error(GENERIC_ERROR);
        renderRegion.innerHTML = nextBody.innerHTML;

        var nextCount = doc.querySelector('[data-ff-cart-count]');
        if (nextCount) setCount(nextCount.textContent, nextCount.hasAttribute('hidden'));

        var nextCartLink = doc.getElementById('cart-icon-bubble');
        var cartLink = document.getElementById('cart-icon-bubble');
        if (nextCartLink && cartLink && nextCartLink.getAttribute('aria-label')) {
          cartLink.setAttribute('aria-label', nextCartLink.getAttribute('aria-label'));
        }

        if (FF.emit) FF.emit('cart:updated', {});
      });
  }

  /* ---------------------------------------------------------------------- */
  /* Open / close                                                            */
  /* ---------------------------------------------------------------------- */

  function isOpen() {
    return !!drawer && drawer.classList.contains('is-open');
  }

  function open() {
    if (!drawer || isOpen()) return;

    var opener = document.activeElement;

    drawer.removeAttribute('inert');
    drawer.removeAttribute('aria-hidden');
    drawer.classList.add('is-open');

    if (FF.lockScroll) FF.lockScroll(true);
    if (FF.trapFocus) releaseFocus = FF.trapFocus(panel, opener);

    window.requestAnimationFrame(function () {
      if (panel) panel.focus();
    });
  }

  function close() {
    if (!drawer || !isOpen()) return;

    drawer.classList.remove('is-open');

    /* Release focus before going inert, or the restore target is unreachable. */
    if (releaseFocus) {
      releaseFocus(true);
      releaseFocus = null;
    }

    drawer.setAttribute('inert', '');
    drawer.setAttribute('aria-hidden', 'true');

    if (FF.lockScroll) FF.lockScroll(false);
    clearError();
  }

  /* ---------------------------------------------------------------------- */
  /* Mutations                                                               */
  /* ---------------------------------------------------------------------- */

  /* `payload` is a FormData (from a product form) or a plain {id, quantity}. */
  function add(payload) {
    clearError();
    setBusy(true);

    var url = (routes.cart_add_url || '/cart/add') + '.js';
    var request;

    if (typeof FormData !== 'undefined' && payload instanceof FormData) {
      request = fetch(url, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: payload
      }).then(readResponse);
    } else {
      request = postJSON(url, payload);
    }

    return request
      .then(function () {
        return refresh();
      })
      .then(function () {
        open();
        announce('Added to your cart.');
      })
      .catch(function (error) {
        open();
        showError(error.message);
      })
      .then(function () {
        setBusy(false);
      });
  }

  function change(line, quantity) {
    clearError();
    setBusy(true);

    return postJSON((routes.cart_change_url || '/cart/change') + '.js', {
      line: line,
      quantity: quantity
    })
      .then(function () {
        return refresh();
      })
      .then(function () {
        announce(quantity === 0 ? 'Item removed.' : 'Cart updated.');
      })
      .catch(function (error) {
        showError(error.message);
        /* Put the stepper back in step with the server. */
        return refresh().catch(function () {});
      })
      .then(function () {
        setBusy(false);
      });
  }

  /* ---------------------------------------------------------------------- */
  /* Events                                                                  */
  /* ---------------------------------------------------------------------- */

  var commitQuantity = FF.debounce
    ? FF.debounce(function (line, quantity) {
        change(line, quantity);
      }, 450)
    : function (line, quantity) {
        change(line, quantity);
      };

  function onClick(event) {
    var target = event.target;
    /* SVG-in-SVG and non-element targets have no closest() in older engines. */
    if (!target || typeof target.closest !== 'function') return;

    var opener = target.closest('[data-ff-cart-open]');
    if (opener) {
      event.preventDefault();
      open();
      return;
    }

    if (!drawer) return;

    if (target.closest('[data-ff-cart-close]')) {
      event.preventDefault();
      close();
      return;
    }

    var remove = target.closest('[data-ff-cart-remove]');
    if (remove && drawer.contains(remove)) {
      event.preventDefault();
      change(parseInt(remove.getAttribute('data-ff-cart-remove'), 10), 0);
      return;
    }

    var step = target.closest('[data-ff-qty-step]');
    if (step && drawer.contains(step)) {
      event.preventDefault();
      var input = step.parentElement.querySelector('[data-ff-qty-input]');
      if (!input) return;

      var next = Math.max(0, (parseInt(input.value, 10) || 0) + parseInt(step.getAttribute('data-ff-qty-step'), 10));
      input.value = next;
      commitQuantity(parseInt(input.getAttribute('data-ff-cart-line'), 10), next);
    }
  }

  function onChange(event) {
    var input = event.target.closest('[data-ff-qty-input]');
    if (!input || !drawer || !drawer.contains(input)) return;

    var quantity = parseInt(input.value, 10);
    if (isNaN(quantity) || quantity < 0) {
      quantity = 0;
      input.value = 0;
    }
    commitQuantity(parseInt(input.getAttribute('data-ff-cart-line'), 10), quantity);
  }

  function onSubmit(event) {
    var form = event.target.closest('form[action*="/cart/add"]');
    if (!form || form.hasAttribute('data-ff-cart-skip')) return;

    event.preventDefault();

    var submitButton = form.querySelector('[type="submit"]');
    if (submitButton) submitButton.setAttribute('disabled', 'disabled');

    add(new FormData(form)).then(function () {
      if (submitButton) submitButton.removeAttribute('disabled');
    });
  }

  function onKeydown(event) {
    if (event.key === 'Escape' && isOpen()) {
      event.stopPropagation();
      close();
    }
  }

  /* ---------------------------------------------------------------------- */
  /* Boot                                                                    */
  /* ---------------------------------------------------------------------- */

  function init() {
    if (!cacheNodes()) return;

    document.addEventListener('click', onClick);
    document.addEventListener('change', onChange);
    document.addEventListener('submit', onSubmit);
    document.addEventListener('keydown', onKeydown);
  }

  window.FFCart = {
    open: open,
    close: close,
    refresh: refresh,
    add: add,
    change: change
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
